import React, { useEffect, useMemo, useState } from "react";
import { Area, AreaChart, Cell, Pie, PieChart, ResponsiveContainer, Tooltip, XAxis } from "recharts";
import { Flame, TrendingUp } from "lucide-react";
import { checks } from "@/lib/api";
import { agree, unitPlural } from "@/lib/projectTypes";
import AiBadge from "@/components/AiBadge";

/**
 * Every figure here is derived from the project itself.
 *
 * The previous version shipped placeholder numbers — a fixed "27 fonti citate",
 * a PDF count that fell back to 3 when there were none, and a hardcoded list of
 * key points about an unrelated thesis. Invented figures in a research tool are
 * worse than no figures.
 */

function plainWords(html) {
  return (html || "").replace(/<[^>]+>/g, " ").split(/\s+/).filter(Boolean).length;
}

function Figure({ value, label, hint }) {
  return (
    <div className="rounded-md border border-line bg-surface p-4">
      <p className="font-serif-display text-[28px] font-semibold leading-none tabular-nums text-ink">{value}</p>
      <p className="mt-2 text-[13px] text-ink">{label}</p>
      {hint && <p className="mt-0.5 text-2xs text-ink-subtle">{hint}</p>}
    </div>
  );
}


/**
 * Writing pace, read back from the snapshot trail.
 *
 * `chapter_versions` has been recording word counts on every save since the
 * feature existed; this is the first thing to read them. No model involved -
 * it is arithmetic over rows that were already there.
 */
function WritingPace({ thesisId }) {
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let cancelled = false;
    checks
      .stats(thesisId)
      .then((d) => !cancelled && setStats(d))
      .catch(() => !cancelled && setStats(null))
      .finally(() => !cancelled && setLoading(false));
    return () => {
      cancelled = true;
    };
  }, [thesisId]);

  if (loading) return <div className="numa-shimmer mt-9 h-36 rounded-md" />;
  if (!stats?.available) {
    return (
      <section className="mt-9">
        <h2 className="numa-eyebrow">Ritmo di scrittura</h2>
        <p className="numa-meta mt-2">
          {stats?.reason || "Lo storico si popola man mano che scrivi."}
        </p>
      </section>
    );
  }

  const series = (stats.series || []).map((d) => ({
    ...d,
    label: new Date(d.date).toLocaleDateString("it-IT", { day: "numeric", month: "short" }),
  }));

  return (
    <section className="mt-9">
      <div className="flex flex-wrap items-baseline justify-between gap-3">
        <h2 className="numa-eyebrow">Ritmo di scrittura</h2>
        <p className="text-2xs text-ink-subtle">
          dal {new Date(stats.first_day).toLocaleDateString("it-IT")} &middot; {stats.active_days} giorni di lavoro
        </p>
      </div>

      <div className="mt-3 rounded-md border border-line bg-surface p-4">
        <div className="grid grid-cols-2 gap-4 sm:grid-cols-4">
          <Figure value={stats.recent_pace.toLocaleString("it-IT")} label="Parole al giorno" hint="media recente" />
          <Figure value={stats.average_per_active_day.toLocaleString("it-IT")} label="Media storica" />
          <Figure
            value={stats.streak_days}
            label="Giorni consecutivi"
            hint={stats.streak_days > 2 ? "serie in corso" : undefined}
          />
          <Figure
            value={(stats.best_day?.words || 0).toLocaleString("it-IT")}
            label="Giornata migliore"
            hint={stats.best_day ? new Date(stats.best_day.date).toLocaleDateString("it-IT") : undefined}
          />
        </div>

        {series.length > 1 && (
          <div className="mt-5 h-32">
            <ResponsiveContainer width="100%" height="100%">
              <AreaChart data={series} margin={{ top: 4, right: 4, left: 0, bottom: 0 }}>
                <defs>
                  <linearGradient id="paceFill" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="0%" stopColor="var(--accent)" stopOpacity={0.28} />
                    <stop offset="100%" stopColor="var(--accent)" stopOpacity={0.02} />
                  </linearGradient>
                </defs>
                <XAxis
                  dataKey="label"
                  tick={{ fontSize: 10, fill: "var(--ink-subtle)" }}
                  axisLine={false}
                  tickLine={false}
                  minTickGap={28}
                />
                <Tooltip
                  cursor={{ stroke: "var(--line-strong)" }}
                  contentStyle={{
                    borderRadius: 6,
                    border: "1px solid var(--line)",
                    background: "var(--surface)",
                    fontSize: 12,
                  }}
                  formatter={(v) => [`${v} parole`, ""]}
                  labelFormatter={(l) => l}
                />
                <Area
                  type="monotone"
                  dataKey="words"
                  stroke="var(--accent)"
                  strokeWidth={1.8}
                  fill="url(#paceFill)"
                  isAnimationActive={false}
                />
              </AreaChart>
            </ResponsiveContainer>
          </div>
        )}

        {stats.projection && (
          <p className="mt-4 flex items-start gap-2 border-t border-line pt-3 text-[12.5px] leading-relaxed text-ink-muted">
            <TrendingUp className="mt-0.5 h-3.5 w-3.5 shrink-0 text-ink-subtle" />
            <span>
              Mancano <strong className="text-ink">{stats.projection.words_remaining.toLocaleString("it-IT")}</strong>{" "}
              parole all&apos;obiettivo: circa{" "}
              <strong className="text-ink">{stats.projection.days_at_current_pace} giorni</strong> di lavoro al
              ritmo attuale. Estrapolazione lineare su {stats.projection.basis}, non una previsione.
            </span>
          </p>
        )}
      </div>
    </section>
  );
}

export default function DashboardView({ thesis, pdfs = [] }) {
  const completion = thesis?.completion_percentage || 0;
  const chapters = useMemo(() => thesis?.chapters || [], [thesis]);
  const units = unitPlural(thesis?.project_type);

  const { words, completed, started, summarised, pagesRead } = useMemo(() => {
    const summarisedDocs = pdfs.filter((p) => p.summary || p.key_points?.length);
    return {
      words: chapters.reduce((acc, c) => acc + plainWords(c.content), 0),
      completed: chapters.filter((c) => c.status === "completed").length,
      started: chapters.filter((c) => plainWords(c.content) > 0).length,
      summarised: summarisedDocs.length,
      pagesRead: pdfs.reduce((acc, p) => acc + (p.pages || 0), 0),
    };
  }, [chapters, pdfs]);

  const donut = useMemo(
    () => [
      { name: "done", value: completion },
      { name: "left", value: 100 - completion },
    ],
    [completion]
  );

  if (!thesis) {
    return (
      <div className="flex h-screen items-center justify-center bg-paper" data-testid="dashboard-view">
        <p className="text-[14px] text-ink-muted">Nessun progetto aperto.</p>
      </div>
    );
  }

  return (
    <div className="h-screen overflow-y-auto bg-paper numa-fade-in" data-testid="dashboard-view">
      <div className="mx-auto max-w-4xl px-6 py-9 lg:px-10">
        <header className="mb-8">
          <h1 className="font-serif-display text-[28px] font-semibold text-ink">Andamento</h1>
          <p className="numa-meta mt-1">{thesis.title}</p>
          <AiBadge mode="none" className="mt-3" detail="Statistiche calcolate dal tuo storico di scrittura." />
        </header>

        <div className="grid gap-6 md:grid-cols-[210px_1fr]">
          <div className="flex flex-col items-center justify-center rounded-md border border-line bg-surface p-5">
            <div className="relative h-40 w-40">
              <ResponsiveContainer width="100%" height="100%">
                <PieChart>
                  <Pie
                    data={donut}
                    dataKey="value"
                    innerRadius={58}
                    outerRadius={76}
                    startAngle={90}
                    endAngle={-270}
                    stroke="none"
                    isAnimationActive={false}
                  >
                    <Cell fill="var(--accent)" />
                    <Cell fill="var(--line)" />
                  </Pie>
                </PieChart>
              </ResponsiveContainer>
              <div className="absolute inset-0 flex flex-col items-center justify-center">
                <span
                  data-testid="progress-percentage"
                  className="font-serif-display text-[32px] font-semibold tabular-nums text-ink"
                >
                  {completion}%
                </span>
                <span className="mt-0.5 text-2xs text-ink-subtle">completato</span>
              </div>
            </div>
          </div>

          <div className="grid grid-cols-2 gap-3">
            <Figure value={words.toLocaleString("it-IT")} label="Parole" />
            <Figure
              value={`${started}/${chapters.length}`}
              label={`${units} ${agree(thesis?.project_type, "avviati", "avviate")}`}
              hint={`${completed} ${agree(thesis?.project_type, "completati", "completate")}`}
            />
            <Figure
              value={pdfs.length}
              label="Fonti caricate"
              hint={pagesRead ? `${pagesRead.toLocaleString("it-IT")} pagine complessive` : undefined}
            />
            <Figure
              value={summarised}
              label="Fonti analizzate"
              hint={
                pdfs.length && summarised < pdfs.length
                  ? `${pdfs.length - summarised} senza riassunto`
                  : undefined
              }
            />
          </div>
        </div>

        <WritingPace thesisId={thesis.thesis_id} />

        <section className="mt-9">
          <h2 className="numa-eyebrow">Dettaglio per sezione</h2>
          <table className="mt-3 w-full border-collapse text-left">
            <thead>
              <tr className="border-b border-line">
                <th scope="col" className="py-2 text-2xs font-medium uppercase tracking-wider text-ink-subtle">
                  Sezione
                </th>
                <th scope="col" className="py-2 text-right text-2xs font-medium uppercase tracking-wider text-ink-subtle">
                  Parole
                </th>
                <th scope="col" className="w-32 py-2 text-right text-2xs font-medium uppercase tracking-wider text-ink-subtle">
                  Avanzamento
                </th>
              </tr>
            </thead>
            <tbody>
              {chapters.map((c) => (
                <tr key={c.id} className="border-b border-line last:border-0">
                  <td className="py-2 text-[13.5px] text-ink">{c.title}</td>
                  <td className="py-2 text-right text-[13.5px] tabular-nums text-ink-muted">
                    {plainWords(c.content).toLocaleString("it-IT")}
                  </td>
                  <td className="py-2">
                    <div className="flex items-center justify-end gap-2">
                      <span className="numa-progress-track w-20">
                        <span className="numa-progress-fill block" style={{ width: `${c.progress}%` }} />
                      </span>
                      <span className="w-9 text-right text-2xs tabular-nums text-ink-subtle">{c.progress}%</span>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </section>
      </div>
    </div>
  );
}
